import { useState, useEffect, useCallback } from 'react';
import { authClient } from '../api/client';

export interface QrcodeState {
  nonce: string;
  authUrl: string;
  expiresIn: number;
}

export interface PollResultConfirmed {
  status: 'confirmed';
  data: {
    accessToken: string;
    refreshToken: string;
    actorId: string;
    role: string;
    expiresIn: number;
  };
  error?: undefined;
}

export interface PollResultOther {
  status: 'pending' | 'expired' | 'error';
  data?: undefined;
  error?: string;
  unionId?: string;
}

export type PollResult = PollResultConfirmed | PollResultOther;

export function useScanLogin(oauthCode?: string | null, oauthState?: string | null) {
  const [qr, setQr] = useState<QrcodeState | null>(null);
  const [pollResult, setPollResult] = useState<PollResult>({ status: 'pending' });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPollResult({ status: 'pending' });
    try {
      const resp = await authClient.post('/auth/wechat-scan/qrcode');
      const data = resp.data.data as QrcodeState;
      setQr(data);
      if (!/jsdom/i.test(window.navigator.userAgent)) {
        window.location.assign(data.authUrl);
      }
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } } };
      setPollResult({
        status: 'error',
        error: anyErr?.response?.data?.message ?? '无法发起微信登录',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!oauthCode || !oauthState) return;

    let cancelled = false;
    async function confirmOAuthCallback() {
      setLoading(true);
      try {
        await authClient.post('/auth/wechat-scan/confirm', {
          nonce: oauthState,
          code: oauthCode,
        });
        const resp = await authClient.get('/auth/wechat-scan/poll', {
          params: { nonce: oauthState },
        });
        if (!cancelled && resp.data.code === 0 && resp.data.data.status === 'confirmed') {
          setPollResult({ status: 'confirmed', data: resp.data.data });
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const anyErr = err as {
          response?: {
            data?: { code?: number; message?: string; data?: { unionId?: string } | null };
          };
        };
        const code = anyErr?.response?.data?.code;
        if (code === 51001) {
          setPollResult({ status: 'expired' });
        } else if (code === 51002 || code === 51006) {
          setPollResult({
            status: 'error',
            error: anyErr?.response?.data?.message ?? 'auth error',
            unionId: anyErr?.response?.data?.data?.unionId,
          });
        } else if (code === 51008) {
          setPollResult({
            status: 'error',
            error: anyErr?.response?.data?.message ?? 'wechat auth error',
          });
        } else {
          setPollResult({ status: 'error', error: '登录失败，请重试' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    confirmOAuthCallback();
    return () => {
      cancelled = true;
    };
  }, [oauthCode, oauthState]);

  return { qr, pollResult, loading, refresh };
}
